import { isDefinedString, reduce } from "et3-type-guards";

import { EFieldTone } from "../../constants/enum/field-tone";
import i18n, { Ti18nKeys } from "../../constants/i18n";
import ET3Div from "../et3-div";
import { withLiteObserverAndTheme } from "../et3-enhanced-render";
import Et3Text from "../et3-text";
import { TEt3Theme } from "../et3-theme";
import {
  FIELD_GRID_BLANK_VALUES,
  FIELD_GRID_CELL_HEIGHT,
  FIELD_GRID_COLUMN_GAP,
  FIELD_GRID_EMPTY_VALUE,
  FIELD_GRID_FALLBACK_LABEL_KEY,
  FIELD_GRID_LABEL_FONT_SIZE,
  FIELD_GRID_MACHINE_FONT_SIZE,
  FIELD_GRID_MONO_FONT_STACK,
  FIELD_GRID_ROW_GAP,
  FIELD_GRID_VALUE_FONT_SIZE
} from "./field-grid-constants";

export type TEt3Field = {
  labelKey: Ti18nKeys;
  value: string;
  tone: EFieldTone;
  isMachineValue: boolean;
};

const getToneColor = (tone: EFieldTone, theme: TEt3Theme) => {
  const { colors } = theme;
  switch (tone) {
    case EFieldTone.POSITIVE:
      return colors.all.green;
    case EFieldTone.WARNING:
      return colors.all.darkOrange;
    case EFieldTone.CRITICAL:
      return colors.all.red;
    default:
      return colors.all.maastrichtBlue;
  }
};

export const isBlankValue = (value: string) => {
  return (
    !isDefinedString(value) ||
    FIELD_GRID_BLANK_VALUES.indexOf(value.trim()) > -1
  );
};

const Et3FieldGrid = withLiteObserverAndTheme<{
  fields: TEt3Field[];
  columns: number;
  width: number;
}>((props) => {
  const { fields, columns, width, theme } = props;
  const { colors, unitX, unitY } = theme;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={width}
      height={0}
      fDirection="row"
      alignItems="flex-start"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{
        boxSizing: "border-box",
        width: "100%",
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        columnGap: FIELD_GRID_COLUMN_GAP * unitX,
        rowGap: FIELD_GRID_ROW_GAP * unitY
      }}
      onClick={() => {}}
    >
      {fields.map((field, index) => {
        const labelKey = reduce(
          () => field.labelKey,
          FIELD_GRID_FALLBACK_LABEL_KEY
        );
        const rawValue = reduce(() => field.value, "");
        const tone = reduce(() => field.tone, EFieldTone.NEUTRAL);
        const isMachineValue = reduce(() => field.isMachineValue, false);
        const isBlank = isBlankValue(rawValue);

        return (
          <ET3Div
            key={`${labelKey}-${index}`}
            isScrollable={false}
            isExtendable={true}
            withShadow={false}
            width={0}
            height={FIELD_GRID_CELL_HEIGHT}
            fDirection="column"
            alignItems="flex-start"
            justifyContent="flex-start"
            color={colors.all.transparent}
            style={{ width: "100%", minWidth: 0 }}
            onClick={() => {}}
          >
            <Et3Text
              fontFamily={"LIGHT"}
              fontSize={FIELD_GRID_LABEL_FONT_SIZE}
              text={i18n.get(labelKey)}
              isSpan={true}
              color={colors.all.davysGrey}
              style={{
                margin: 0,
                textTransform: "uppercase",
                letterSpacing: 0.4
              }}
            />
            <Et3Text
              fontFamily={isMachineValue && !isBlank ? "LIGHT" : "MEDIUM"}
              fontSize={
                isMachineValue && !isBlank
                  ? FIELD_GRID_MACHINE_FONT_SIZE
                  : FIELD_GRID_VALUE_FONT_SIZE
              }
              text={isBlank ? FIELD_GRID_EMPTY_VALUE : rawValue}
              isSpan={true}
              color={isBlank ? colors.all.gray : getToneColor(tone, theme)}
              style={{
                margin: 0,
                overflowWrap: "anywhere",
                textAlign: "left",
                fontFamily:
                  isMachineValue && !isBlank
                    ? FIELD_GRID_MONO_FONT_STACK
                    : undefined
              }}
            />
          </ET3Div>
        );
      })}
    </ET3Div>
  );
});
export default Et3FieldGrid;
